import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import CardMedia from "@mui/material/CardMedia";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "react-quill/dist/quill.bubble.css";
import { Search as SearchIcon } from "react-feather";
import { darken } from "polished";
import FormControl from "@mui/material/FormControl";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormLabel from "@mui/material/FormLabel";
import { Formik } from "formik";
import * as Yup from "yup";

import { axiosInstance } from "../../../utils/axios";
import { useSpace } from "../../../contexts/SpaceContext";
import useAuth from "../../../hooks/useAuth";

import {
  Grid,
  CardActions,
  CardContent,
  Card as MuiCard,
  CardHeader as MuiCardHeader,
  Breadcrumbs as MuiBreadcrumbs,
  Paper as MuiPaper,
  Divider as MuiDivider,
  TextField as MuiTextField,
  DialogActions,
  Dialog,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableRow,
  InputBase,
  FormControlLabel,
  FormHelperText,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  InputLabel,
  Switch,
} from "@mui/material";

import { tableCellClasses } from "@mui/material/TableCell";

import {
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Groups as GroupsIcon,
  RecordVoiceOver as RecordVoiceOverIcon,
  Group as GroupIcon,
  Diversity3 as Diversity3Icon,
  Devices as DevicesIcon,
} from "@mui/icons-material";

import { spacing } from "@mui/system";

const TIMEZONE_OPTIONS = [
  { value: "Pacific/Midway", label: "Midway Island, Samoa (GMT-11:00)" },
  { value: "Pacific/Honolulu", label: "Hawaii (GMT-10:00)" },
  { value: "America/Anchorage", label: "Alaska (GMT-09:00)" },
  {
    value: "America/Los_Angeles",
    label: "Pacific Time (US & Canada) (GMT-08:00)",
  },
  { value: "America/Denver", label: "Mountain Time (US & Canada) (GMT-07:00)" },
  { value: "America/Chicago", label: "Central Time (US & Canada) (GMT-06:00)" },
  {
    value: "America/New_York",
    label: "Eastern Time (US & Canada) (GMT-05:00)",
  },
  { value: "America/Halifax", label: "Atlantic Time (Canada) (GMT-04:00)" },
  {
    value: "America/Argentina/Buenos_Aires",
    label: "Buenos Aires (GMT-03:00)",
  },
  { value: "Atlantic/South_Georgia", label: "Mid-Atlantic (GMT-02:00)" },
  { value: "Atlantic/Azores", label: "Azores (GMT-01:00)" },
  { value: "Europe/London", label: "London, Edinburgh, Dublin (GMT+00:00)" },
  { value: "Europe/Paris", label: "Paris, Brussels, Copenhagen (GMT+01:00)" },
  { value: "Europe/Helsinki", label: "Helsinki, Athens, Cairo (GMT+02:00)" },
  { value: "Asia/Kuwait", label: "Kuwait, Riyadh, Moscow (GMT+03:00)" },
  { value: "Asia/Dubai", label: "Abu Dhabi, Muscat (GMT+04:00)" },
  { value: "Asia/Kolkata", label: "Mumbai, New Delhi (GMT+05:30)" },
  { value: "Asia/Kathmandu", label: "Kathmandu (GMT+05:45)" },
  { value: "Asia/Dhaka", label: "Dhaka (GMT+06:00)" },
  { value: "Asia/Bangkok", label: "Bangkok, Jakarta (GMT+07:00)" },
  { value: "Asia/Shanghai", label: "Beijing, Singapore (GMT+08:00)" },
  { value: "Asia/Tokyo", label: "Tokyo, Seoul (GMT+09:00)" },
  { value: "Australia/Sydney", label: "Sydney, Melbourne (GMT+10:00)" },
  { value: "Pacific/Noumea", label: "New Caledonia (GMT+11:00)" },
  { value: "Pacific/Auckland", label: "Auckland (GMT+12:00)" },
  { value: "Pacific/Fakaofo", label: "Tokelau (GMT+13:00)" },
];

const Header = styled.div`
  padding: ${(props) => props.theme.spacing(6)} 0;
`;

const Card = styled(MuiCard)(spacing);

const CardHeader = styled(MuiCardHeader)(spacing);

const TextField = styled(MuiTextField)(spacing);

const Divider = styled(MuiDivider)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Paper = styled(MuiPaper)(spacing);

const CustomTableCell = styled(TableCell)`
  &.${tableCellClasses.head} {
    background: ${(props) => props.theme.palette.common.black};
    color: ${(props) => props.theme.palette.common.white};
  }
  &.${tableCellClasses.body} {
    font-size: 14px;
  }
`;

// Styled icon container for event type cards
const EventTypeIconContainer = styled.div`
  height: 140px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) =>
    props.gradient || "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"};
  border-radius: 4px 4px 0 0;
`;

// Updated GraphQL mutation to include classification
const CREATE_EVENT_TYPE_MUTATION = `
  mutation CreateEventType($input: CreateEventTypeInput!) {
    createEventType(input: $input) {
      eventType {
        id
        title
        duration
        description
        url
        teamId
        timezone
        classification {
          eventFormat
          templateType
          customTemplate
        }
      }
    }
  }
`;

const CustomTableRow = styled(TableRow)`
  // &:nth-of-type(odd) {
  //   background-color: rgba(0, 0, 0, 0.025);
  // }
`;

// Data
let id = 0;
function createData(name, calories, fat, carbs, protein) {
  id += 1;
  return { id, name, calories, fat, carbs, protein };
}

const rows = [
  createData("Frozen yoghurt", 159, 6.0, 24, 4.0),
  createData("Ice cream sandwich", 237, 9.0, 37, 4.3),
  createData("Eclair", 262, 16.0, 24, 6.0),
  createData("Cupcake", 305, 3.7, 67, 4.3),
  createData("Gingerbread", 356, 16.0, 49, 3.9),
];

const EventType = (props) => {
  const { doChangeProps, selectedEventFormat, setSelectedEventFormat } = props;

  const handleProceed = (value) => {
    console.log("EventType: Proceed button clicked with event format:", value);
    // Store the selected event format
    setSelectedEventFormat(value);
    doChangeProps("1");
  };

  return (
    <div>
      <Header>
        <Typography variant="h3" gutterBottom align="center">
          What kind of event type is it?
        </Typography>
        <Typography variant="subtitle1" gutterBottom align="center">
          Choose the type of the event type you would like to create.
        </Typography>
      </Header>

      <Grid container justifyContent="center">
        <Grid item xs={12} lg={10}>
          <Grid container spacing={6} alignItems="flex-end">
            <Grid item xs={12} md={4}>
              <Card p={5}>
                <EventTypeIconContainer gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)">
                  <PersonIcon sx={{ fontSize: 48, color: "white", mr: 1 }} />
                  <PersonIcon sx={{ fontSize: 48, color: "white" }} />
                </EventTypeIconContainer>
                <CardHeader
                  title="One To One"
                  titleTypographyProps={{ align: "center" }}
                  pb={0}
                  pt={2}
                />
                <CardContent>
                  <Typography variant="subtitle1" align="center">
                    Host a 1:1 meeting between you and a single attendee.
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="primary"
                    onClick={() => handleProceed("one-to-one")}
                  >
                    Proceed
                  </Button>
                </CardActions>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card p={5}>
                <EventTypeIconContainer gradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)">
                  <RecordVoiceOverIcon sx={{ fontSize: 56, color: "white" }} />
                  <GroupsIcon
                    sx={{ fontSize: 48, color: "white", ml: 1, opacity: 0.8 }}
                  />
                </EventTypeIconContainer>
                <CardHeader
                  title="One To Many"
                  titleTypographyProps={{ align: "center" }}
                  pb={0}
                  pt={2}
                />
                <CardContent>
                  <Typography variant="subtitle1" align="center">
                    Host a talk or presentation with one host and multiple
                    attendees.
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="primary"
                    onClick={() => handleProceed("one-to-many")}
                  >
                    Proceed
                  </Button>
                </CardActions>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card p={5}>
                <EventTypeIconContainer gradient="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)">
                  <GroupIcon sx={{ fontSize: 48, color: "white", mr: 1 }} />
                  <PersonIcon sx={{ fontSize: 56, color: "white" }} />
                </EventTypeIconContainer>
                <CardHeader
                  title="Many To One"
                  titleTypographyProps={{ align: "center" }}
                  pb={0}
                  pt={2}
                />
                <CardContent>
                  <Typography variant="subtitle1" align="center">
                    Multiple hosts meeting with a single attendee.
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="primary"
                    onClick={() => handleProceed("many-to-one")}
                  >
                    Proceed
                  </Button>
                </CardActions>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card p={5}>
                <EventTypeIconContainer gradient="linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)">
                  <Diversity3Icon sx={{ fontSize: 64, color: "white" }} />
                </EventTypeIconContainer>
                <CardHeader
                  title="Many To Many"
                  titleTypographyProps={{ align: "center" }}
                  pb={0}
                  pt={2}
                />
                <CardContent>
                  <Typography variant="subtitle1" align="center">
                    Multiple hosts and multiple attendees in a group meeting.
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="primary"
                    onClick={() => handleProceed("many-to-many")}
                  >
                    Proceed
                  </Button>
                </CardActions>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card p={5}>
                <EventTypeIconContainer gradient="linear-gradient(135deg, #fa709a 0%, #fee140 100%)">
                  <DevicesIcon sx={{ fontSize: 48, color: "white", mr: 1 }} />
                  <GroupsIcon sx={{ fontSize: 48, color: "white" }} />
                </EventTypeIconContainer>
                <CardHeader
                  title="Hybrid"
                  titleTypographyProps={{ align: "center" }}
                  pb={0}
                  pt={2}
                />
                <CardContent>
                  <Typography variant="subtitle1" align="center">
                    Both in-person and virtual attendees can join your event.
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="primary"
                    onClick={() => handleProceed("hybrid")}
                  >
                    Proceed
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

const EventSubType = (props) => {
  const { doChangeProps, selectedTemplateType, setSelectedTemplateType } =
    props;

  const handleStart = (value, templateType) => {
    console.log(
      "EventSubType: Start button clicked with template:",
      templateType
    );
    // Store the selected template type
    setSelectedTemplateType(templateType);
    doChangeProps(value);
  };

  return (
    <div>
      <Header>
        <Typography variant="h3" gutterBottom align="center">
          Start building your event type
        </Typography>
        <Typography variant="subtitle1" gutterBottom align="center">
          Start from scratch or pick a template to set up your event faster.
        </Typography>
      </Header>

      <Grid container justifyContent="center">
        <Grid item xs={12} lg={12}>
          <Grid container spacing={6} alignItems="flex-end">
            <Grid item xs={12} md={6}>
              <Card p={5}>
                <Grid container>
                  <Grid item md={4}>
                    <CardMedia
                      component="img"
                      height="140"
                      image="https://assets-versions-cdn.static.events.ringcentral.com/assets/packs/media/react/features/event-creation-wizard/assets/templates/blank-f8f34e8a582e7b3b073f04f1220db88e.png"
                      alt="blank template"
                    />
                  </Grid>
                  <Grid item md={8}>
                    <CardHeader
                      title="Start with a blank event"
                      pb={0}
                      pt={2}
                    />
                    <CardContent>
                      <Typography variant="subtitle1">
                        Create a completely custom event type from scratch.
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleStart("2", "blank")}
                      >
                        Start
                      </Button>
                    </CardActions>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card p={5}>
                <Grid container>
                  <Grid item md={4}>
                    <CardMedia
                      component="img"
                      height="140"
                      image="https://assets-versions-cdn.static.events.ringcentral.com/assets/packs/media/react/features/event-creation-wizard/assets/templates/expo-887d82b4e95ad9c41975bcb541c831f2.png"
                      alt="expo template"
                    />
                  </Grid>
                  <Grid item md={8}>
                    <CardHeader title="Expos and tradeshows" pb={0} pt={2} />
                    <CardContent>
                      <Typography variant="subtitle1">
                        Create an event type optimized for expos, trade shows,
                        and exhibitions.
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button size="small" variant="outlined">
                        Learn More
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleStart("2", "expo-tradeshow")}
                      >
                        Start
                      </Button>
                    </CardActions>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
            {/* Other event types could be added here */}
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

function CustomizedTableDemo() {
  return (
    <Card mb={6} mt={6}>
      <Paper>
        <Table>
          <TableBody>
            {rows.map((row) => (
              <CustomTableRow key={row.id}>
                <CustomTableCell component="th" scope="row">
                  UTC -12:00
                </CustomTableCell>
                <CustomTableCell align="right">
                  Canada/Yukon Time
                  <br />
                  <small style={{ color: "rgba(0, 0, 0, 0.6)" }}>
                    (UTC-07:00) Yukon
                  </small>
                </CustomTableCell>
              </CustomTableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Card>
  );
}
const Search = styled("div")`
  border-radius: 2px;
  background-color: ${(props) => darken(0.05, props.theme.header.background)};
  position: relative;
  width: 100%;

  &:hover {
    background-color: ${(props) => darken(0.05, props.theme.header.background)};
  }
`;

const SearchIconWrapper = styled("div")`
  width: 50px;
  height: 100%;
  position: absolute;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 22px;
    height: 22px;
  }
`;
const Input = styled(InputBase)`
  color: inherit;
  width: 100%;
  > input {
    color: ${(props) => props.theme.header.search.color};
    padding-top: ${(props) => props.theme.spacing(2.5)};
    padding-right: ${(props) => props.theme.spacing(2.5)};
    padding-bottom: ${(props) => props.theme.spacing(2.5)};
    padding-left: ${(props) => props.theme.spacing(12)};
  }
`;

// GraphQL query to get availabilities
const GET_AVAILABILITIES = `
  query GetAllAvailabilities {
    getAllAvailabilities {
      id
      name
      timezone
    }
  }
`;

const EventEntryForm = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentSpace } = useSpace();
  const { user } = useAuth();

  const { alert, setAlert, selectedEventFormat, selectedTemplateType } = props;

  // Get spaceId and teamId from query params
  const queryParams = new URLSearchParams(location.search);
  const currentSpaceId = queryParams.get("spaceId") || currentSpace?.id;
  const teamId = queryParams.get("teamId"); // Extract teamId from query params

  const [open, setOpen] = useState(false);
  const [teamInfo, setTeamInfo] = useState(null);
  const [isUrlManuallyEdited, setIsUrlManuallyEdited] = useState(false);
  const [availabilities, setAvailabilities] = useState([]);
  const [selectedAvailabilityId, setSelectedAvailabilityId] = useState("");
  const [formValues, setFormValues] = useState({
    title: "",
    url: "",
    description: "",
    duration: 30,
    assignment: "Collective",
    timezone: "America/New_York",
    showInWidget: false,
  });

  // Fetch custom availabilities (excluding "Default Availability")
  useEffect(() => {
    const fetchAvailabilities = async () => {
      try {
        const response = await axiosInstance.post("/graphql", {
          query: GET_AVAILABILITIES,
        });

        // Handle different response structures
        let availabilityData = null;
        if (response.data?.data?.getAllAvailabilities) {
          availabilityData = response.data.data.getAllAvailabilities;
        } else if (response.data?.getAllAvailabilities) {
          availabilityData = response.data.getAllAvailabilities;
        }

        if (availabilityData && availabilityData.length > 0) {
          // Filter out "Default Availability" - user must use their own custom availability
          const customAvailabilities = availabilityData.filter(
            (a) => a.name !== "Default Availability"
          );
          setAvailabilities(customAvailabilities);

          // If only one custom availability, auto-select it
          if (customAvailabilities.length === 1) {
            setSelectedAvailabilityId(customAvailabilities[0].id);
          }

          // Auto-select timezone from the first availability (or the only one if just one exists)
          const defaultAvailability =
            customAvailabilities.length > 0
              ? customAvailabilities[0]
              : availabilityData[0];

          if (defaultAvailability?.timezone) {
            setFormValues((prev) => ({
              ...prev,
              timezone: defaultAvailability.timezone,
            }));
          }
        }
      } catch (error) {
        console.error("Error fetching availabilities:", error);
      }
    };

    fetchAvailabilities();
  }, []);

  // Fetch team info if teamId is provided
  useEffect(() => {
    const fetchTeamInfo = async () => {
      if (!teamId) return;

      try {
        const response = await axiosInstance.post("/graphql", {
          query: `
            query GetTeam($id: ID!) {
              getTeam(id: $id) {
                id
                name
              }
            }
          `,
          variables: { id: teamId },
        });

        if (response.data?.getTeam) {
          setTeamInfo(response.data.getTeam);
        }
      } catch (error) {
        console.error("Error fetching team info:", error);
      }
    };

    if (teamId) {
      fetchTeamInfo();
    }
  }, [teamId]);

  // Function to convert string to slug
  const convertToSlug = (text) => {
    return text
      .toLowerCase()
      .replace(/\s+/g, "-") // Replace spaces with -
      .replace(/[^\w\-]+/g, "") // Remove all non-word chars
      .replace(/\-\-+/g, "-") // Replace multiple - with single -
      .replace(/^-+/, "") // Trim - from start of text
      .replace(/-+$/, ""); // Trim - from end of text
  };

  // Auto-generate URL from title
  useEffect(() => {
    if (formValues.title && !isUrlManuallyEdited) {
      const slug = convertToSlug(formValues.title);

      setFormValues((prevValues) => ({
        ...prevValues,
        url: slug,
      }));
    }
  }, [formValues.title, isUrlManuallyEdited]);

  // Function to validate and normalize URL
  const validateAndNormalizeUrl = (url) => {
    if (!url) return "";

    // Format the URL (lowercase, replace spaces with hyphens, remove special chars)
    return url
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9\-]/g, "");
  };

  // Helper to check if URL is valid
  const isValidUrl = (url) => {
    // Basic format validation - allows letters, numbers, and hyphens
    const urlPattern = /^[a-z0-9\-]+$/;
    return urlPattern.test(url);
  };

  // Handle form submission
  const handleFormSubmit = async (
    values,
    { setErrors, setStatus, setSubmitting }
  ) => {
    try {
      console.log("Form submitted with values:", values);
      console.log("Using spaceId:", currentSpaceId);
      console.log("Using teamId:", teamId);
      console.log("Event format:", selectedEventFormat);
      console.log("Template type:", selectedTemplateType);

      // Include spaceId, teamId, availabilityId, and classification in the mutation input
      const eventTypeInput = {
        title: values.title,
        url: values.url,
        description: values.description,
        duration: parseInt(values.duration),
        assignment: values.assignment,
        timezone: values.timezone,
        spaceId: currentSpaceId,
        availabilityId: selectedAvailabilityId, // Use the selected availability
        showInWidget: formValues.showInWidget,
        // Add the classification information
        classification: {
          eventFormat: selectedEventFormat || "one-to-one", // Default to one-to-one if not selected
          templateType: selectedTemplateType || "blank", // Default to blank if not selected
          customTemplate: "", // For future use if we allow custom templates
        },
      };

      // Add teamId if available
      if (teamId) {
        eventTypeInput.teamId = teamId;
      }

      console.log("Submitting with input:", eventTypeInput);
      console.log("Mutation:", CREATE_EVENT_TYPE_MUTATION);

      const response = await axiosInstance.post("/graphql", {
        query: CREATE_EVENT_TYPE_MUTATION,
        variables: { input: eventTypeInput },
      });

      console.log("API Response:", response);

      if (response.data?.errors) {
        throw new Error(response.data.errors[0].message);
      }

      // Handle successful response
      setStatus({ success: true });
      setAlert({
        open: true,
        message: "Event Type Created Successfully!",
        severity: "success",
      });

      // Navigate back to event types list with the spaceId in the query
      // Include filter params if it was a team event
      let redirectUrl = `/calendar/event-types?spaceId=${currentSpaceId}`;
      if (teamId) {
        redirectUrl += `&filterType=team&teamId=${teamId}`;
      }

      navigate(redirectUrl, {
        state: { message: "Event Type created Successfully" },
      });
    } catch (error) {
      console.error("Error creating event type:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "An unexpected error occurred";
      setStatus({ success: false });
      setErrors({ submit: errorMessage });
      setAlert({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle form field changes directly
  const handleTitleChange = (e, setFieldValue) => {
    const newTitle = e.target.value;
    setFieldValue("title", newTitle);

    // Update formValues state for the useEffect to use
    setFormValues((prevValues) => ({
      ...prevValues,
      title: newTitle,
    }));
  };

  // Handle URL field changes
  const handleUrlChange = (e, setFieldValue) => {
    setIsUrlManuallyEdited(true);
    // Format the URL properly
    const formattedUrl = e.target.value
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9\-\/]/g, "");

    setFieldValue("url", formattedUrl);

    // Update formValues state
    setFormValues((prevValues) => ({
      ...prevValues,
      url: formattedUrl,
    }));
  };

  // Handle description changes from ReactQuill
  const handleDescriptionChange = (content, setFieldValue) => {
    setFieldValue("description", content);

    // Update formValues state
    setFormValues((prevValues) => ({
      ...prevValues,
      description: content,
    }));
  };

  return (
    <Formik
      initialValues={formValues}
      validationSchema={Yup.object().shape({
        title: Yup.string().max(255).required("Title is required"),
        url: Yup.string().required("URL is required"),
        description: Yup.string().required("Description is required"),
        duration: Yup.number()
          .min(5, "Duration must be at least 5 minutes")
          .max(60, "Duration cannot exceed 60 minutes")
          .required("Duration is required"),
        assignment: Yup.string().required("Assignment type is required"),
        timezone: Yup.string().required("Timezone is required"),
      })}
      enableReinitialize={true}
      onSubmit={handleFormSubmit}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values,
        setFieldValue,
      }) => (
        <form noValidate onSubmit={handleSubmit}>
          <Card mb={6}>
            <CardContent>
              {teamId && teamInfo && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Creating an event type for team:{" "}
                  <strong>{teamInfo.name}</strong>
                </Alert>
              )}

              {/* Show selected event format and template */}
              {selectedEventFormat && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Event Format:{" "}
                  <strong>
                    {selectedEventFormat
                      .replace("-", " ")
                      .replace(/\b\w/g, (l) => l.toUpperCase())}
                  </strong>
                </Alert>
              )}

              {selectedTemplateType && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Using Template:{" "}
                  <strong>
                    {selectedTemplateType === "blank"
                      ? "Blank Event"
                      : selectedTemplateType === "expo-tradeshow"
                      ? "Expos and Tradeshows"
                      : selectedTemplateType}
                  </strong>
                </Alert>
              )}

              <Grid container spacing={6}>
                <Grid item md={6}>
                  <TextField
                    id="title"
                    name="title"
                    label="Title"
                    placeholder="Give your event an exciting name"
                    variant="outlined"
                    fullWidth
                    value={values.title}
                    error={Boolean(touched.title && errors.title)}
                    helperText={touched.title && errors.title}
                    onBlur={handleBlur}
                    onChange={(e) => handleTitleChange(e, setFieldValue)}
                    my={2}
                  />
                </Grid>
                <Grid item md={6}>
                  <TextField
                    id="url"
                    name="url"
                    label="Url"
                    placeholder="Type your unique url (letters, numbers, hyphens)"
                    variant="outlined"
                    fullWidth
                    value={values.url}
                    error={Boolean(touched.url && errors.url)}
                    helperText={
                      (touched.url && errors.url) ||
                      "URL slug is auto-generated from title. This will be your event's booking page URL."
                    }
                    onBlur={handleBlur}
                    onChange={(e) => handleUrlChange(e, setFieldValue)}
                    my={2}
                    InputProps={{
                      startAdornment: (
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{ mr: 1 }}
                        >
                          {window.location.origin}/preview/events/preview/
                          {user?.name?.toLowerCase().replace(/\s+/g, "-") ||
                            "username"}
                          /
                        </Typography>
                      ),
                    }}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={3} sx={{ mt: 2, mb: 4 }}>
                <Grid item md={12}>
                  <FormLabel>Description</FormLabel>
                  <div style={{ marginTop: "8px", marginBottom: "60px" }}>
                    <ReactQuill
                      style={{ height: 150 }}
                      theme="snow"
                      value={values.description}
                      onChange={(content) =>
                        handleDescriptionChange(content, setFieldValue)
                      }
                      placeholder="Type something.."
                    />
                  </div>
                  {touched.description && errors.description && (
                    <FormHelperText error>{errors.description}</FormHelperText>
                  )}
                </Grid>
              </Grid>

              <Grid container spacing={3} sx={{ mt: 4 }}>
                <Grid item md={6}>
                  <FormControl
                    fullWidth
                    variant="outlined"
                    error={Boolean(touched.duration && errors.duration)}
                  >
                    <InputLabel id="duration-label">Duration</InputLabel>
                    <Select
                      labelId="duration-label"
                      id="duration"
                      name="duration"
                      value={values.duration}
                      label="Duration"
                      onBlur={handleBlur}
                      onChange={(e) => {
                        handleChange(e);
                        // Update formValues state
                        setFormValues((prevValues) => ({
                          ...prevValues,
                          duration: e.target.value,
                        }));
                      }}
                    >
                      {[5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60].map(
                        (minutes) => (
                          <MenuItem key={minutes} value={minutes}>
                            {minutes} minutes
                          </MenuItem>
                        )
                      )}
                    </Select>
                    {touched.duration && errors.duration && (
                      <FormHelperText>{errors.duration}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid item md={6}>
                  <FormControl
                    fullWidth
                    variant="outlined"
                    error={Boolean(touched.timezone && errors.timezone)}
                  >
                    <InputLabel id="timezone-label">Timezone</InputLabel>
                    <Select
                      labelId="timezone-label"
                      id="timezone"
                      name="timezone"
                      value={values.timezone}
                      label="Timezone"
                      onBlur={handleBlur}
                      onChange={(e) => {
                        handleChange(e);
                        // Update formValues state
                        setFormValues((prevValues) => ({
                          ...prevValues,
                          timezone: e.target.value,
                        }));
                      }}
                    >
                      {TIMEZONE_OPTIONS.map((timezone) => (
                        <MenuItem key={timezone.value} value={timezone.value}>
                          {timezone.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {touched.timezone && errors.timezone && (
                      <FormHelperText>{errors.timezone}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              </Grid>

              {/* Availability Selection */}
              <Grid container spacing={3} sx={{ mt: 2 }}>
                <Grid item md={6}>
                  <FormControl
                    fullWidth
                    variant="outlined"
                    error={!selectedAvailabilityId && availabilities.length > 0}
                  >
                    <InputLabel id="availability-label">
                      Availability
                    </InputLabel>
                    <Select
                      labelId="availability-label"
                      id="availability"
                      name="availability"
                      value={selectedAvailabilityId}
                      label="Availability"
                      onChange={(e) =>
                        setSelectedAvailabilityId(e.target.value)
                      }
                      disabled={availabilities.length <= 1}
                    >
                      {availabilities.map((availability) => (
                        <MenuItem key={availability.id} value={availability.id}>
                          {availability.name}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {availabilities.length === 0
                        ? "No custom availability found. Please create one first."
                        : availabilities.length === 1
                        ? `Using: ${availabilities[0].name}`
                        : "Select the availability schedule for this event type"}
                    </FormHelperText>
                  </FormControl>
                </Grid>
              </Grid>

              <Grid container spacing={3} sx={{ mt: 2 }}>
                <Grid item md={6}>
                  <FormControl>
                    <FormLabel>Assignment</FormLabel>
                    <RadioGroup
                      aria-labelledby="assignment-radio-group-label"
                      name="assignment"
                      value={values.assignment}
                      onChange={(e) => {
                        handleChange(e);
                        // Update formValues state
                        setFormValues((prevValues) => ({
                          ...prevValues,
                          assignment: e.target.value,
                        }));
                      }}
                    >
                      <FormControlLabel
                        value="Collective"
                        control={<Radio />}
                        label="Collective"
                      />
                      <FormControlLabel
                        value="Round Robin"
                        control={<Radio />}
                        label="Round Robin"
                      />
                      <FormControlLabel
                        value="Managed Event"
                        control={<Radio />}
                        label="Managed Event"
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
              </Grid>

              {/* Show in Widget Toggle */}
              <Grid container spacing={3} sx={{ mt: 2 }}>
                <Grid item md={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formValues.showInWidget}
                        onChange={(e) =>
                          setFormValues((prev) => ({
                            ...prev,
                            showInWidget: e.target.checked,
                          }))
                        }
                      />
                    }
                    label="Show in Widget"
                  />
                  <FormHelperText>
                    Allow visitors to book this event type through the website
                    widget
                  </FormHelperText>
                </Grid>
              </Grid>

              <Paper mt={4}>
                <div>
                  <Dialog
                    open={open}
                    onClose={() => setOpen(false)}
                    aria-labelledby="form-dialog-title"
                  >
                    <DialogTitle id="form-dialog-title">
                      Timezone
                      <br />
                      <small style={{ color: "rgba(0, 0, 0, 0.6)" }}>
                        Set the timezone in which your event is occurring
                      </small>
                    </DialogTitle>
                    <DialogContent>
                      <Typography>
                        <Search className="searchBar">
                          <SearchIconWrapper>
                            <SearchIcon />
                          </SearchIconWrapper>
                          <Input placeholder="Search for a country or a timezone" />
                        </Search>
                      </Typography>
                      <CustomizedTableDemo />
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={() => setOpen(false)} color="primary">
                        Close
                      </Button>
                    </DialogActions>
                  </Dialog>
                </div>
              </Paper>

              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isSubmitting}
                onClick={() => {
                  console.log("Create Event button clicked");
                  console.log("Form values:", values);
                  console.log("Form errors:", errors);
                }}
              >
                Create Event
              </Button>

              {errors.submit && (
                <Typography color="error" sx={{ mt: 2 }}>
                  {errors.submit}
                </Typography>
              )}
            </CardContent>
          </Card>
        </form>
      )}
    </Formik>
  );
};

function EventTypeAdd() {
  const location = useLocation();
  const { currentSpace } = useSpace();

  // Get spaceId from query params or use current space
  const queryParams = new URLSearchParams(location.search);
  const spaceId = queryParams.get("spaceId") || currentSpace?.id;
  const teamId = queryParams.get("teamId");

  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false });
  };

  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [eventType, setEventType] = useState("0");

  // Add state for the selected event format and template type
  const [selectedEventFormat, setSelectedEventFormat] = useState(null);
  const [selectedTemplateType, setSelectedTemplateType] = useState(null);

  const handleEventTypeChange = (newType) => {
    console.log("Changing event type from", eventType, "to", newType);
    setEventType(newType);
  };

  // Fetch team info if teamId is provided
  const [teamInfo, setTeamInfo] = useState(null);

  useEffect(() => {
    const fetchTeamInfo = async () => {
      if (!teamId) return;

      try {
        const response = await axiosInstance.post("/graphql", {
          query: `
            query GetTeam($id: ID!) {
              getTeam(id: $id) {
                id
                name
              }
            }
          `,
          variables: { id: teamId },
        });

        if (response.data?.getTeam) {
          setTeamInfo(response.data.getTeam);
        }
      } catch (error) {
        console.error("Error fetching team info:", error);
      }
    };

    if (teamId) {
      fetchTeamInfo();
    }
  }, [teamId]);

  return (
    <React.Fragment>
      <Helmet title="Add New Event Type" />
      <Grid justifyContent="space-between" container spacing={10}>
        <Grid item>
          <Typography variant="h3" gutterBottom display="inline">
            Add New Event Type
            {teamInfo && (
              <Typography
                variant="h5"
                color="primary"
                display="inline"
                sx={{ ml: 1 }}
              >
                for team: {teamInfo.name}
              </Typography>
            )}
          </Typography>

          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} to={`/spaces/${spaceId}`}>
              {currentSpace?.name || "Spaces"}
            </Link>
            <Link
              component={NavLink}
              to={`/calendar/dashboard?spaceId=${spaceId}`}
            >
              Dashboard
            </Link>
            <Link
              component={NavLink}
              to={`/calendar/event-types?spaceId=${spaceId}`}
            >
              Events Type List
            </Link>
            <Typography>Add New Event Type</Typography>
          </Breadcrumbs>
        </Grid>
        <Grid item>
          <div>
            <Button
              variant="contained"
              color="inherit"
              sx={{ mx: "0.5rem" }}
              component={Link}
              to={`/calendar/event-types?spaceId=${spaceId}`}
            >
              <ArrowBackIcon />
              Back
            </Button>
          </div>
        </Grid>
      </Grid>
      <Divider my={6} />
      <Grid container spacing={6}>
        <Grid item xs={12}>
          {eventType === "0" ? (
            <EventType
              doChangeProps={handleEventTypeChange}
              selectedEventFormat={selectedEventFormat}
              setSelectedEventFormat={setSelectedEventFormat}
            />
          ) : eventType === "1" ? (
            <EventSubType
              doChangeProps={handleEventTypeChange}
              selectedTemplateType={selectedTemplateType}
              setSelectedTemplateType={setSelectedTemplateType}
            />
          ) : (
            <EventEntryForm
              alert={alert}
              setAlert={setAlert}
              selectedEventFormat={selectedEventFormat}
              selectedTemplateType={selectedTemplateType}
            />
          )}
        </Grid>
      </Grid>
      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
      >
        <Alert onClose={handleCloseAlert} severity={alert.severity}>
          {alert.message}
        </Alert>
      </Snackbar>
    </React.Fragment>
  );
}

export default EventTypeAdd;
